import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  MenuItem,
  TextField,
} from "@mui/material";
import CustomBreadcrumbs from "../../common/components/breadcrumb-component-new/breadcrumb-component-new";
import OrderItemCard from "./component/OrderItemCard";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getOrderSummaryById,
  deleteOrderItemByServiceId,
  deleteOrderItemByOrderItemId,
  createUserBillingDetails,
  updateServiceDate,
} from "../../api/order-summary.api";
import moment from "moment-timezone";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { getCartCount } from "../../api/cart.api";
import { useDispatch, useSelector } from "react-redux";
import DeleteServicePopup from "../cart-page/components/DeleteServicePopup";
import PriceDetails from "./component/PriceDetails";
import BillingDetails from "./component/BillingDetails";
import { ReactComponent as EditIcon } from "../../assets/icons/edit-icon.svg";
import { ReactComponent as BillingCallIcon } from "../../assets/icons/billing-call.svg";
import { ReactComponent as BillingCalendarIcon } from "../../assets/icons/billing-calendar.svg";
import { ReactComponent as BillingAddressIcon } from "../../assets/icons/address.svg";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import { maskPhoneNumber } from "../../utils/utils";
import { getUserProfileDetails } from "../../api/userProfile.api";
import ToastMessage from "../../common/components/toast-message/toast-message";
import "./order-summary-page.scss";

export default function OrderSummaryPage() {
  const [orderSummary, setOrderSummary] = useState<any>(null);
  const [orderId, setOrderId] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [orderCount, setOrderCount] = useState<number>(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState<() => void>(() => { });

  const [billingId, setBillingId] = useState<number>(0);
  const [billingPopupOpen, setBillingPopupOpen] = useState(false);
  const [billingFormValid, setBillingFormValid] = useState(false);
  const [billingAdded, setBillingAdded] = useState(false);
  const [billingFormData, setBillingFormData] = useState<any>(null);
  const [isBillingPresent, setIsBillingPresent] = useState<boolean>(false);
  const [billingDetails, setBillingDetails] = useState<any>(null);
  const [profileDetails, setProfileDetails] = useState<any>(null);
  const [serviceDateType, setServiceDateType] = useState<string>("no-date");
  const [serviceDate, setServiceDate] = useState<string>("");
  const [serviceHour, setServiceHour] = useState<string>("12");
  const [serviceMinute, setServiceMinute] = useState<string>("00");
  const [serviceMeridiem, setServiceMeridiem] = useState<"AM" | "PM">("AM");
  const systemTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [timezone] = useState<string>(systemTimezone);
  const [serviceDateLoading, setServiceDateLoading] = useState<boolean>(false);
  const [minDate, setMinDate] = useState<string>(
    moment().tz(systemTimezone).format("YYYY-MM-DD"),
  );
  const today = new Date().toISOString().split("T")[0];
  const [initialServiceDate, setInitialServiceDate] = useState<string>("");
  const [initialServiceDateType, setInitialServiceDateType] =
    useState<string>("no-date");
  const [initialServiceHour, setInitialServiceHour] = useState<string>("12");
  const [initialServiceMinute, setInitialServiceMinute] =
    useState<string>("00");
  const [initialServiceMeridiem, setInitialServiceMeridiem] = useState<
    "AM" | "PM"
  >("AM");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "error",
  });
  const [isServiceDateValid, setIsServiceDateValid] = useState<boolean>(true);

  const userLocation = useSelector(
    (state: {
      userLocation: { latitude: number; longitude: number; city: string };
    }) => state.userLocation,
  );

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const orderIdPlaceOrder = location.state?.orderId;
  const isFromPlans = location.state?.isFromPlans ?? false;
  const plannerId = location.state?.plannerId;

  const isFromCartPage = location.state?.isFromCartPage ?? false;
  const isFromOrderSummary = location.state?.isFromOrderSummary ?? false;
  const isBackFromPaymentPg = location.state?.isBackFromPaymentPg ?? false;
  const partnerId = location.state?.partnerId;
  const isFromDetailsPg = location.state?.isFromDetailsPg ?? false;

  const effectivePartnerId = partnerId ?? plannerId;

  const profileBillingDetails = profileDetails?.billingDetails;

  let userDetails: any = null;
  try {
    const storedUser = localStorage.getItem("userDetails");
    if (storedUser) userDetails = JSON.parse(storedUser);
  } catch (err) {
    console.error("Failed to parse userData from localStorage", err);
  }

  const formatTo12Hour = (dateTime: string, tz?: string) => {
    const m = tz ? moment.tz(dateTime, tz) : moment(dateTime);

    let hour24 = m.hour();
    const minute = String(m.minute()).padStart(2, "0");
    const meridiem: "AM" | "PM" = hour24 >= 12 ? "PM" : "AM";

    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;

    return {
      date: m.format("YYYY-MM-DD"),
      hour: String(hour12).padStart(2, "0"),
      minute,
      meridiem,
    };
  };

  const convert12HourTo24Hour = (
    hour: string,
    minute: string,
    meridiem: "AM" | "PM",
  ) => {
    let h = parseInt(hour, 10);

    if (meridiem === "AM") {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h += 12;
    }

    return `${String(h).padStart(2, "0")}:${minute}:00`;
  };

  const requestDeleteService = (orderId: number, serviceId: number) => {
    setPopupMessage(
      "Are you sure you want to delete this service and all its sub-services?",
    );
    setConfirmAction(() => async () => {
      try {
        await deleteOrderItemByServiceId(orderId, serviceId);
        fetchOrderSummary();
        fetchCartCount();
      } catch (err) {
        console.error(err);
      }
    });
    setPopupOpen(true);
  };

  const requestDeleteSubService = (orderId: number, subServiceId: number) => {
    setPopupMessage("Are you sure you want to delete this sub-service?");
    setConfirmAction(() => async () => {
      try {
        await deleteOrderItemByOrderItemId(orderId, subServiceId);
        fetchOrderSummary();
        fetchCartCount();
      } catch (err) {
        console.error(err);
      }
    });
    setPopupOpen(true);
  };

  const fetchOrderSummary = async () => {
    setLoading(true);
    try {
      const { data } = await getOrderSummaryById(Number(orderIdPlaceOrder));

      if (data?.success) {
        const orders = data?.data?.data?.orders;
        const billingData = orders?.billingAddress;

        if (billingData) {
          setIsBillingPresent(true);
          setBillingFormData(billingData);
          setBillingDetails(billingData);
          setBillingAdded(true);
          setBillingFormValid(true);
        } else {
          setIsBillingPresent(false);
          setBillingAdded(false);
          setBillingDetails(null);
          setBillingFormData(null);
          setBillingFormValid(false);
        }

        const partners = orders?.partners;

        if (orders && Array.isArray(partners) && partners.length > 0) {
          setOrderId(orders.orderId);
          setOrderCount(partners.length);
          setOrderSummary({
            partners: partners,
            summary: orders.summary,
          });

          if (orders.serviceDateTime) {
            const parsed = formatTo12Hour(orders.serviceDateTime, timezone);

            setServiceDateType("specify");
            setServiceDate(parsed.date);
            setServiceHour(parsed.hour);
            setServiceMinute(parsed.minute);
            setServiceMeridiem(parsed.meridiem);

            setInitialServiceDate(parsed.date);
            setInitialServiceHour(parsed.hour);
            setInitialServiceMinute(parsed.minute);
            setInitialServiceMeridiem(parsed.meridiem);
            setInitialServiceDateType("specify");
          } else {
            setServiceDateType("no-date");
            setServiceDate("");
            setServiceHour("12");
            setServiceMinute("00");
            setServiceMeridiem("AM");

            setInitialServiceDate("");
            setInitialServiceHour("12");
            setInitialServiceMinute("00");
            setInitialServiceMeridiem("AM");
            setInitialServiceDateType("no-date");
          }
        } else {
          setOrderId(orders?.orderId ?? 0);
          setOrderCount(Array.isArray(partners) ? partners.length : 0);
          setOrderSummary({
            partners: Array.isArray(partners) ? partners : [],
            summary: orders?.summary ?? null,
          });

          if (orders?.serviceDateTime) {
            const parsed = formatTo12Hour(orders.serviceDateTime, timezone);

            setServiceDateType("specify");
            setServiceDate(parsed.date);
            setServiceHour(parsed.hour);
            setServiceMinute(parsed.minute);
            setServiceMeridiem(parsed.meridiem);

            setInitialServiceDate(parsed.date);
            setInitialServiceHour(parsed.hour);
            setInitialServiceMinute(parsed.minute);
            setInitialServiceMeridiem(parsed.meridiem);
            setInitialServiceDateType("specify");
          } else {
            setServiceDateType("no-date");
            setServiceDate("");
            setServiceHour("12");
            setServiceMinute("00");
            setServiceMeridiem("AM");

            setInitialServiceDate("");
            setInitialServiceHour("12");
            setInitialServiceMinute("00");
            setInitialServiceMeridiem("AM");
            setInitialServiceDateType("no-date");
          }
        }
      } else {
        setOrderId(0);
        setOrderSummary({ partners: [], summary: null });
      }
    } catch (error) {
      console.error("Error fetching order summary:", error);
      setOrderId(0);
      setOrderSummary({ partners: [], summary: null });
    } finally {
      setLoading(false);
    }
  };

  const isProfileBillingAddress =
    profileDetails?.billingAddress == null && billingDetails != null;

  const fetchCartCount = async () => {
    try {
      const res = await getCartCount();
      const cartCount = res.data?.data?.data?.totalCount ?? 0;
      dispatch({ type: "CART:SET_COUNT", payload: { count: cartCount } });
    } catch (error) {
      console.error("Failed to fetch cart count:", error);
    }
  };

  const handleSaveBillingDetails = async (formValues: any) => {
    try {
      if (!orderIdPlaceOrder) {
        console.error("Order ID not available");
        return;
      }

      const payload = {
        orderId: orderIdPlaceOrder,
        billingAddress: {
          address: formValues.address,
          country: formValues.country,
          city: formValues.city,
          first_name: formValues.first_name,
          last_name: formValues.last_name,
          phoneNumber: formValues.phoneNumber,
          pincode: formValues.pincode,
          state: formValues.state,
        },
      };

      const response = await createUserBillingDetails(payload);

      if (response?.data?.success) {
        fetchOrderSummary();
        setBillingFormData(formValues);
        setBillingAdded(true);
        setBillingFormValid(true);
        setBillingPopupOpen(false);
      } else {
        console.error("API failed:", response);
      }
    } catch (error) {
      console.error("Error saving billing details:", error);
    }
  };

  const fetchUserProfileDetails = async () => {
    try {
      const response = await getUserProfileDetails();
      if (response?.data?.success) {
        const profileData = response.data?.data;
        setProfileDetails(profileData);
      }
    } catch (error) {
      console.error("USER PROFILE GET FAILED::", error);
    }
  };

  const handleServiceDateSubmit = async () => {
    if (!orderIdPlaceOrder) return;
    setServiceDateLoading(true);

    try {
      const isDateSpecified = serviceDateType === "specify";

      const payload: any = {
        isDateSpecified,
        timeZone: timezone,
      };

      if (isDateSpecified) {
        const timePart = convert12HourTo24Hour(
          serviceHour,
          serviceMinute,
          serviceMeridiem,
        );

        const dateTimeStr = `${serviceDate}T${timePart}`;

        const selectedMoment = moment.tz(dateTimeStr, timezone);
        const nowInTz = moment.tz(timezone);

        if (selectedMoment.isSameOrBefore(nowInTz)) {
          setIsServiceDateValid(false);
          setToast({
            open: true,
            message: "Please select a future date and time.",
            severity: "error",
          });
          setServiceDateLoading(false);
          return;
        }

        setIsServiceDateValid(true);
        payload.serviceDateTime = selectedMoment.format("YYYY-MM-DDTHH:mm:ss");
      } else {
        setIsServiceDateValid(true);
      }

      const response = await updateServiceDate(
        Number(orderIdPlaceOrder),
        payload,
      );

      if (response?.data?.success) {
        setInitialServiceDate(isDateSpecified ? serviceDate : "");
        setInitialServiceHour(isDateSpecified ? serviceHour : "12");
        setInitialServiceMinute(isDateSpecified ? serviceMinute : "00");
        setInitialServiceMeridiem(isDateSpecified ? serviceMeridiem : "AM");
        setInitialServiceDateType(serviceDateType);

        if (!isDateSpecified) {
          setServiceDate("");
          setServiceHour("12");
          setServiceMinute("00");
          setServiceMeridiem("AM");
        }

        setIsServiceDateValid(true);

        setToast({
          open: true,
          message: "Service date updated successfully!",
          severity: "success",
        });
      } else {
        setIsServiceDateValid(false);
        setToast({
          open: true,
          message: "Failed to update service date.",
          severity: "error",
        });
      }
    } catch (error: any) {
      console.error("Error updating service date:", error);
      setIsServiceDateValid(false);
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.code === "VALIDATION_ERROR"
          ? "Invalid service date & time"
          : errorData?.message ||
          "An error occurred while updating the service date.";

      setToast({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setServiceDateLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfileDetails();
  }, []);

  useEffect(() => {
    fetchOrderSummary();
    const timer = setTimeout(() => {
      fetchCartCount();
    }, 1000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userDetails?.stripe_customer_id) return;

    if (userDetails.stripe_customer_id) {
      return;
    }
  }, [userDetails]);

  useEffect(() => {
    if (!orderIdPlaceOrder) return;
    if (loading) return;

    const typeChanged = serviceDateType !== initialServiceDateType;
    const dateChanged = serviceDate !== initialServiceDate;
    const hourChanged = serviceHour !== initialServiceHour;
    const minuteChanged = serviceMinute !== initialServiceMinute;
    const meridiemChanged = serviceMeridiem !== initialServiceMeridiem;

    if (
      !typeChanged &&
      !dateChanged &&
      !hourChanged &&
      !minuteChanged &&
      !meridiemChanged
    ) {
      return;
    }

    if (serviceDateType === "specify" && !serviceDate) return;

    const timer = setTimeout(() => {
      handleServiceDateSubmit();
    }, 400);

    return () => clearTimeout(timer);
  }, [
    serviceDateType,
    serviceDate,
    serviceHour,
    serviceMinute,
    serviceMeridiem,
    initialServiceDate,
    initialServiceHour,
    initialServiceMinute,
    initialServiceMeridiem,
    initialServiceDateType,
    orderIdPlaceOrder,
    loading,
  ]);

  useEffect(() => {
    if (serviceDateType === "no-date") {
      setIsServiceDateValid(true);
      return;
    }

    if (!serviceDate) {
      setIsServiceDateValid(false);
      return;
    }

    const timePart = convert12HourTo24Hour(
      serviceHour,
      serviceMinute,
      serviceMeridiem,
    );

    const selectedMoment = moment.tz(`${serviceDate}T${timePart}`, timezone);
    const nowInTz = moment.tz(timezone);

    setIsServiceDateValid(selectedMoment.isAfter(nowInTz));
  }, [
    serviceDateType,
    serviceDate,
    serviceHour,
    serviceMinute,
    serviceMeridiem,
    timezone,
  ]);

  if (loading) {
    return (
      <Container className="order-summary">
        <Box className="order-summary__loading">
          <CircularProgress
            size="3rem"
            className="spinner-details-page__spinner"
          />
        </Box>
      </Container>
    );
  }

  if (!orderSummary || orderSummary.partners.length === 0) {
    return (
      <Container className="cart-page">
        <Box className="cart-page__empty-page">
          <Box className="cart-page__empty">
            <Box className="cart-page__empty-inner">
              <Typography className="cart-page__empty-title">
                Your order is empty
              </Typography>
              <Typography className="cart-page__empty-subtitle">
                Looks like you have not made any choice.
              </Typography>
              <Box className="cart-page__empty-btn-wrapper">
                <Button
                  className="cart-page__empty-btn"
                  onClick={() => {
                    navigate("/home/list-of-funeral-homes");
                  }}
                >
                  Back to list
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    );
  }

  const getBreadcrumbItems = () => {
    const orderSummaryItem = {
      label: "Order summary",
      state: { partnerId: effectivePartnerId },
    };

    if (isFromPlans) {
      return [
        {
          label: "Detail page",
          to: `/home/list-of-funeral-homes/preneed-planners/${plannerId}`,
        },
        { label: "Selected pacakge", to: `/cart` },
        { label: "Order summary", state: { plannerId: plannerId } },
      ];
    }

    if (isBackFromPaymentPg) {
      return [{ label: "Selected Services", to: "/cart" }, orderSummaryItem];
    }

    if (isFromDetailsPg && effectivePartnerId) {
      return [
        {
          label: "Detail page",
          to: `/home/list-of-funeral-homes/details/${effectivePartnerId}`,
        },
        { label: "Selected Services", to: "/cart" },
        orderSummaryItem,
      ];
    }

    if (isFromCartPage) {
      return [{ label: "Selected Services", to: "/cart" }, orderSummaryItem];
    }
    if (isFromOrderSummary) {
      return [
        {
          label: "Order History",
          to: `/order-history`,
        },
        { label: `Order Details`, to: `/order-history/${orderId}` },
        orderSummaryItem,
      ];
    }

    if (effectivePartnerId) {
      return [
        { label: "Home", to: "/" },
        { label: "List", to: "/home/list-of-funeral-homes" },
        {
          label: "Detail page",
          to: `/home/list-of-funeral-homes/details/${effectivePartnerId}`,
        },
        orderSummaryItem,
      ];
    }

    return [{ label: "Home", to: "/" }, orderSummaryItem];
  };

  return (
    <Container className="order-summary">
      <Box className="order-summary__breadcrumbs">
        <CustomBreadcrumbs items={getBreadcrumbItems()} />
      </Box>

      <Box className="order-summary__content">
        <Box className="order-summary__left">
          <Typography className="order-summary__section-title">
            Service Date
          </Typography>
          <Box className="order-summary__service-date">
            <Box className="service-date-card">
              <Box className="service-date-option">
                <Box
                  className={`radio-button ${serviceDateType === "specify" ? "active" : ""
                    }`}
                  onClick={() => setServiceDateType("specify")}
                />
                <Box className="service-date-input-group">
                  <Typography className="option-label">
                    Specify Service Date & Time
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "12px",
                      marginTop: "12px",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: "220px" }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={
                            serviceDateType === "specify" && serviceDate
                              ? dayjs(serviceDate)
                              : null
                          }
                          onChange={(newValue: Dayjs | null) => {
                            setServiceDateType("specify");
                            if (newValue) {
                              setServiceDate(newValue.format("YYYY-MM-DD"));
                            } else {
                              setServiceDate("");
                            }
                          }}
                          minDate={dayjs(minDate)}
                          format="MM/DD/YYYY"
                          disabled={serviceDateType !== "specify"}
                          slotProps={{
                            textField: {
                              error: serviceDateType === "specify" && !isServiceDateValid,
                              fullWidth: true,
                              size: "small",
                              placeholder: "MM/DD/YYYY",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  backgroundColor:
                                    serviceDateType !== "specify"
                                      ? "#f9fafb"
                                      : "#fff",
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>

                    <TextField
                      select
                      label="Hour"
                      size="small"
                      value={serviceHour}
                      onChange={(e) => {
                        setServiceDateType("specify");
                        setServiceHour(e.target.value);
                      }}
                      disabled={serviceDateType !== "specify"}
                      error={serviceDateType === "specify" && !isServiceDateValid}
                      sx={{ minWidth: 90 }}
                    >
                      {Array.from({ length: 12 }, (_, i) => {
                        const val = String(i + 1).padStart(2, "0");
                        return (
                          <MenuItem key={val} value={val}>
                            {val}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    <TextField
                      select
                      label="Minute"
                      size="small"
                      value={serviceMinute}
                      onChange={(e) => {
                        setServiceDateType("specify");
                        setServiceMinute(e.target.value);
                      }}
                      disabled={serviceDateType !== "specify"}
                      error={serviceDateType === "specify" && !isServiceDateValid}
                      sx={{ minWidth: 90 }}
                    >
                      {Array.from({ length: 60 }, (_, i) => {
                        const val = String(i).padStart(2, "0");
                        return (
                          <MenuItem key={val} value={val}>
                            {val}
                          </MenuItem>
                        );
                      })}
                    </TextField>

                    <TextField
                      select
                      label="AM/PM"
                      size="small"
                      value={serviceMeridiem}
                      onChange={(e) => {
                        setServiceDateType("specify");
                        setServiceMeridiem(e.target.value as "AM" | "PM");
                      }}
                      disabled={serviceDateType !== "specify"}
                      error={serviceDateType === "specify" && !isServiceDateValid}
                      sx={{ minWidth: 100 }}
                    >
                      <MenuItem value="AM">AM</MenuItem>
                      <MenuItem value="PM">PM</MenuItem>
                    </TextField>
                  </Box>

                  {serviceDateType === "specify" &&
                    serviceDate &&
                    !isServiceDateValid && (
                      <Typography
                        sx={{
                          color: "#d32f2f",
                          fontSize: "0.75rem",
                          marginTop: "6px",
                        }}
                      >
                        Please select a future date and time.
                      </Typography>
                    )}
                </Box>
              </Box>

              <Box className="service-date-option">
                <Box
                  className={`radio-button ${serviceDateType === "no-date" ? "active" : ""
                    }`}
                  onClick={() => {
                    setServiceDateType("no-date");
                    setServiceDate("");
                    setServiceHour("12");
                    setServiceMinute("00");
                    setServiceMeridiem("AM");
                  }}
                />
                <Typography className="option-label">
                  Proceed without date
                </Typography>
              </Box>
            </Box>
          </Box>

          <Typography className="order-summary__section-title">
            Billing Details
          </Typography>

          {isProfileBillingAddress && (
            <Alert severity="warning">
              <AlertTitle sx={{ margin: 0 }}>
                Please add Billing Address in the Profile Page
              </AlertTitle>
            </Alert>
          )}

          <Box className="order-summary__billing">
            {!billingAdded || !billingDetails ? (
              <Button
                variant="outlined"
                className="order-summary__add-billing-btn"
                onClick={() => setBillingPopupOpen(true)}
              >
                Add Billing Details
              </Button>
            ) : (
              <Box className="order-summary__billing-summary">
                <Box className="order-summary__billing-summary-left">
                  <Typography className="order-summary__billing-name">
                    {[billingDetails?.first_name, billingDetails?.last_name]
                      .filter((v) => v && v.trim())
                      .join(" ") || "-"}
                  </Typography>

                  <Box className="order-summary__billing-summary-details">
                    <Box className="billing-summary-item">
                      <Box className="icon-wrapper">
                        <BillingCallIcon />
                      </Box>
                      <span>
                        +1 {maskPhoneNumber(billingDetails?.phoneNumber)}
                      </span>
                    </Box>

                    <Box className="billing-summary-item">
                      <Box className="icon-wrapper">
                        <BillingCalendarIcon />
                      </Box>
                      <span>
                        {billingDetails?.updated_at || billingDetails?.createdAt
                          ? new Date(
                            billingDetails?.updated_at ||
                            billingDetails?.createdAt,
                          ).toLocaleString("en-US", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })
                          : ""}
                      </span>
                    </Box>

                    <Box className="billing-summary-item">
                      <Box className="icon-wrapper">
                        <BillingAddressIcon />
                      </Box>
                      <span>
                        {billingDetails?.address || billingDetails?.street},{" "}
                        {billingDetails?.city}, {billingDetails?.state},{" "}
                        {billingDetails?.pincode || billingDetails?.zipcode}
                      </span>
                    </Box>
                  </Box>
                </Box>

                <Box
                  className="order-summary__billing-summary-edit"
                  onClick={() => setBillingPopupOpen(true)}
                >
                  <EditIcon />
                </Box>
              </Box>
            )}
          </Box>

          <Box className="order-summary__orders">
            <Typography className="order-summary__section-title">
              Orders ({orderCount})
            </Typography>
            <Box className="order-summary__shopping-list">
              {orderSummary.partners.map((partner: any) => (
                <OrderItemCard
                  key={
                    partner.partnerId ??
                    partner.plannerId ??
                    partner.orderItemId
                  }
                  item={partner}
                  orderId={orderId}
                  onDeleteService={requestDeleteService}
                  onDeleteSubService={requestDeleteSubService}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box className="order-summary__right">
          <PriceDetails
            orderSummary={{
              ...orderSummary.summary,
              orderId: orderId,
              partnerList: orderSummary.partners,
            }}
            partnerId={effectivePartnerId}
            billingValid={isBillingPresent}
            isFromCartPage={isFromCartPage}
            profileDetails={profileDetails}
            serviceDate={serviceDate}
            serviceDateType={serviceDateType}
            billingDetails={billingDetails}
            isFromPlans={isFromPlans}
            plannerId={plannerId}
            isServiceDateValid={isServiceDateValid}
          />
        </Box>
      </Box>

      <DeleteServicePopup
        open={popupOpen}
        message={popupMessage}
        onClose={() => setPopupOpen(false)}
        onConfirm={() => {
          confirmAction();
          setPopupOpen(false);
        }}
      />

      <Dialog
        open={billingPopupOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setBillingPopupOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <BillingDetails
            initialValues={billingFormData}
            onFormValidChange={setBillingFormValid}
            onFormDataChange={setBillingFormData}
            onAddBillingDetails={handleSaveBillingDetails}
            onClose={() => setBillingPopupOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <ToastMessage
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </Container>
  );
}
